import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import SearchFilterUtil from 'lib/SearchFilterUtil';
import { validateMinMax } from 'lib/Validations';
import CreatableSelect from 'components/commons/CreatableSelect';
import { useResetErrorsOnClear } from 'hooks/useResetErrors';

function RangeCreatableSelect({
    minOptions,
    maxOptions,
    minValue,
    maxValue,
    onChange,
    formatter = (value) => value,
    validateInput,
    onError,
    labelMin = 'Min',
    labelMax = 'Max',
    sx,
    maxInputLength,
    dropdownDisplayFormatter = SearchFilterUtil.getDisplayValue,
}) {
    const [minError, setMinError] = useState('');
    const [maxError, setMaxError] = useState('');

    // Initial validation on mount
    // This ensures that if the component is rendered with initial values,
    useEffect(() => {
        if (!validateInput) return;
        const minErr = minValue?.value ? validateInput(minValue.value) : '';
        const maxErr = maxValue?.value ? validateInput(maxValue.value) : '';
        setMinError(prev => prev !== minErr ? minErr : prev);
        setMaxError(prev => prev !== maxErr ? maxErr : prev);
        if (minErr || maxErr) {
            onError?.([minErr, maxErr].filter(Boolean));
        }
    }, []);

    useResetErrorsOnClear(validateInput, minError, maxError, minValue, maxValue, setMinError, setMaxError);

    const validateField = (key, value, otherKey, otherValue, setError, otherError) => {
        const _otherValue = typeof otherValue === 'string' ? otherValue : otherValue?.value;
        const _value = typeof value === 'string' ? value : value?.value;
        if (validateInput) {
            let error = '';

            // Only validate input format if no comparison error and value is not empty
            if (!error && _value !== '' && _value !== undefined) {
                error = validateMinMax(key,
                        SearchFilterUtil.getNumericValue(formatter(_value)),
                        SearchFilterUtil.getNumericValue(formatter(_otherValue)))
                    || validateInput(_value);
            }

            setError(error || '');
            if (onError) {
                const errors = key === 'min'
                    ? [error, otherError]
                    : [otherError, error];
                onError(errors.filter(e => Boolean(e)));
            }
        }
    };

    const createHandleChange = (key, setError, otherKey, otherError) => (onChange, otherValue) => (_, value) => {
        validateField(key, value, otherKey, otherValue, setError, otherError);

        let _value = SearchFilterUtil.getNumericValue(formatter(value?.value || ''));
        let formattedValue = _value && value?.value && formatter
            ? { ...value, value: _value }
            : value;

        if (typeof value === 'string') {
            // manual input case
            _value = SearchFilterUtil.getNumericValue(formatter(value || ''));
            formattedValue = {
                label: value,
                value: _value,
            };
        }

        onChange({
            [key]: formattedValue,
            [otherKey]: otherValue
        });
    };

    const handleMinChange = createHandleChange('min', setMinError, 'max', maxError);
    const handleMaxChange = createHandleChange('max', setMaxError, 'min', minError);

    return (
        <>
            <Box sx={{display: 'flex', gap: 2}}>
                <CreatableSelect
                    options={minOptions}
                    value={dropdownDisplayFormatter(minOptions, minValue, formatter, minError)}
                    onChange={handleMinChange(onChange, maxValue)}
                    label={labelMin}
                    error={!!minError}
                    helperText={minError}
                    filterOptions={(options, params) => SearchFilterUtil.filterOptions(options, params)}
                    getOptionDisabled={SearchFilterUtil.getMinOptionDisabled(maxValue)}
                    isOptionEqualToValue={SearchFilterUtil.isOptionEqualToValue}
                    selected={minValue}
                    sx={sx || {width: 150}}
                    maxInputLength={maxInputLength}
                />
                <CreatableSelect
                    options={maxOptions}
                    value={dropdownDisplayFormatter(maxOptions, maxValue, formatter, maxError)}
                    onChange={handleMaxChange(onChange, minValue)}
                    label={labelMax}
                    error={!!maxError}
                    helperText={maxError}
                    filterOptions={(options, params) => SearchFilterUtil.filterOptions(options, params)}
                    getOptionDisabled={SearchFilterUtil.getMaxOptionDisabled(minValue)}
                    isOptionEqualToValue={SearchFilterUtil.isOptionEqualToValue}
                    selected={maxValue}
                    sx={sx || {width: 150}}
                    maxInputLength={maxInputLength}
                />
            </Box>
            {
                (minError || maxError) &&
                <Typography variant="textError2">{ minError || maxError }</Typography>
            }
        </>
    );
}

RangeCreatableSelect.propTypes = {
    minOptions: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
    maxOptions: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
    minValue: PropTypes.object,
    maxValue: PropTypes.object,
    sx: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    formatter: PropTypes.func,
    validateInput: PropTypes.func,
    onError: PropTypes.func,
    dropdownDisplayFormatter: PropTypes.func,
    labelMin: PropTypes.string,
    labelMax: PropTypes.string,
    maxInputLength: PropTypes.number,
};

export default RangeCreatableSelect;